import { keccak256 } from "js-sha3";
import {
  Button,
  TextField,
  FormField,
  Select,
  ActionBar,
  ErrorBanner,
  EmptyState,
  Badge,
} from "frontend-kit/components/index.js";
import { getBlockHeight, getChainApp } from "./chain-app.js";
import {
  connectedMoonlightAccount,
  duskToLux,
  luxToDusk,
  isPrivilegedCreator,
  predictionMarketOwnerAddress,
  predictionMarketCreateAdminAddress,
  readParticipationBondState,
  walletHasCreateRight,
} from "./chain-config.js";
import { base58ToBytes } from "./trader-crypto.js";
import { duskAmountNode } from "./dusk-amount.js";
import {
  loadBackendConfig,
  saveBackendConfig,
  listMarkets,
  postMarketMetadata,
} from "./gateway-api.js";
import { showGlobalTx, trackTx } from "./tx-status.js";
import { renderWenEventCard } from "./wen-event-card.js";
import { createCategoryOptions, onChainCategoryLabel } from "./category-colors.js";

/** Approximate DuskDS block interval for close-time presets. */
const BLOCK_SECONDS = 10;
const BLOCKS_PER_HOUR = Math.round(3600 / BLOCK_SECONDS);
/** Dusk consensus epoch length (stake.rs `EPOCH`). */
const BLOCKS_PER_EPOCH = 2160;

function moonlightPkBytesFromWallet(wallet) {
  const account =
    wallet?.state?.selectedAddress || connectedMoonlightAccount(wallet);
  if (!account) return null;
  const bytes = base58ToBytes(account);
  if (bytes.length !== 96) {
    throw new Error("Connected Moonlight public key must be 96 bytes");
  }
  return bytes;
}

/** Map on-chain create_market panics to portal copy. */
function mapCreateError(err) {
  const msg = err?.message || String(err);
  if (/cooldown active/i.test(msg)) {
    return "Create cooldown active - wait until the next epoch gap before creating another event.";
  }
  if (/participation bond required/i.test(msg)) {
    return "Participation bond required - post the creator bond in Account settings first.";
  }
  if (/lacks the right/i.test(msg)) {
    return "Create right required - claim the streak achievement NFT on Achievements first.";
  }
  return msg;
}

function epochOf(block) {
  return Math.floor(Number(block) / BLOCKS_PER_EPOCH);
}

/** First block of the next epoch after `block` (matches stake next_epoch). */
function nextEpochStart(block) {
  const h = Number(block);
  const rem = h % BLOCKS_PER_EPOCH;
  return rem === 0 ? h + BLOCKS_PER_EPOCH : h + (BLOCKS_PER_EPOCH - rem);
}

const FIELD_TIPS = {
  title: "Short question traders see on cards and the event page.",
  description: "Longer context shown under About. Not committed on-chain.",
  category:
    "Named policy category. Uncategorized (0) is always allowed. Other names appear only after the owner enables them on-chain.",
  criteria:
    "How the YES/NO outcome is judged. Owner uses this when asserting or reviewing disputes.",
  liquidity: "Seed DUSK split evenly into YES/NO pool reserves. Paid from this wallet.",
  close:
    "When trading stops. Hours/days/weeks/date use ~10s/block. Epoch uses 2,160 blocks/epoch. Blocks is a raw tip offset or absolute height.",
  challenge:
    "Blocks after Pending YES/NO for dispute or late challenge before quiet finalize.",
  review: "Blocks after a dispute for owner resolve; timeout keeps status-quo pending outcome.",
  minBond: "Minimum DUSK bond to dispute YES or challenge default NO.",
  feeBps:
    "LP trading fee, skimmed from every buy/sell into this event's fee pot (claim_lp_fees). Max 1000 (10%).",
  apiKey:
    "Bearer for a keyed gateway (CHAIN_GATEWAY_API_KEY). Prefer metadata Basic user/pass when posting to api.wen behind nginx.",
  metadataBasic:
    "nginx /markets/metadata htpasswd (deploy-runbook §5.4). Needed when Create runs off wen.nocturne-standards.org and posts straight to api.wen.",
};

function tipBtn(text) {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "tip-btn";
  b.textContent = "?";
  b.title = text;
  b.setAttribute("aria-label", text);
  b.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    const existing = b.parentElement?.querySelector(".tip-pop");
    if (existing) {
      existing.remove();
      return;
    }
    document.querySelectorAll(".tip-pop").forEach((el) => el.remove());
    const pop = document.createElement("div");
    pop.className = "tip-pop";
    pop.setAttribute("role", "tooltip");
    pop.textContent = text;
    const host = b.parentElement;
    if (host) {
      if (getComputedStyle(host).position === "static") {
        host.style.position = "relative";
      }
      host.append(pop);
    }
    const close = (ev) => {
      if (ev.target === b || pop.contains(ev.target)) return;
      pop.remove();
      document.removeEventListener("click", close);
    };
    setTimeout(() => document.addEventListener("click", close), 0);
  });
  return b;
}

/** Attach a ? tooltip to an existing FormField / Select label. */
function attachTip(fieldEl, tip) {
  const labelEl = fieldEl.querySelector(".form-label, label");
  if (!labelEl || !tip) return fieldEl;
  labelEl.classList.add("create-label-with-tip");
  labelEl.append(tipBtn(tip));
  return fieldEl;
}

function textAreaField({ id, label, tip, value = "", required = false, rows = 6 }) {
  const ta = document.createElement("textarea");
  ta.id = id;
  ta.name = id;
  ta.className = "form-control create-textarea";
  ta.rows = rows;
  ta.value = value;
  if (required) ta.required = true;
  const field = FormField({ id, label, required, control: ta });
  return attachTip(field, tip);
}

/** Canonical metadata JSON + hashes (matches scripts/pm-create-market.sh). */
export function buildMarketCommitment({
  title,
  description,
  resolutionCriteria,
  imageUri = "",
}) {
  const meta = {
    description: description || "",
    image_uri: imageUri || "",
    resolution_criteria: resolutionCriteria || "",
    title: title || "",
  };
  const ordered = {};
  for (const k of Object.keys(meta).sort()) ordered[k] = meta[k];
  const canonical = JSON.stringify(ordered);
  const questionHashHex = keccak256(canonical);
  let contentCidHex = "0".repeat(64);
  if (imageUri) {
    const enc = new TextEncoder();
    const prefix = enc.encode("pm-content-v1");
    const uri = enc.encode(imageUri);
    const buf = new Uint8Array(prefix.length + uri.length);
    buf.set(prefix, 0);
    buf.set(uri, prefix.length);
    contentCidHex = keccak256(buf);
  }
  const questionHash = [];
  const contentCid = [];
  for (let i = 0; i < 64; i += 2) {
    questionHash.push(parseInt(questionHashHex.slice(i, i + 2), 16));
    contentCid.push(parseInt(contentCidHex.slice(i, i + 2), 16));
  }
  return {
    meta,
    canonical,
    questionHashHex,
    contentCidHex,
    questionHash,
    contentCid,
  };
}

/**
 * Poll the gateway indexer for the event's assigned market_id.
 * @returns {Promise<{ id: number, confirmed: boolean }>} `confirmed: false`
 *   means the indexer never showed a matching `question_hash` within the
 *   poll window - `id` is only a guess (current market count), not a
 *   verified on-chain id. Callers must not treat an unconfirmed id as a
 *   safe navigation target - the tx already succeeded on-chain regardless
 *   (see submit()'s tx-confirmation step above this call), it's purely
 *   the indexer lagging or pointed at a different/stale contract.
 */
async function waitForMarketId(config, questionHashHex, expectedId, log) {
  for (let i = 0; i < 12; i++) {
    try {
      const markets = await listMarkets(config);
      const found = markets.find(
        (m) =>
          String(m.question_hash || "")
            .replace(/^0x/i, "")
            .toLowerCase() === questionHashHex.toLowerCase()
      );
      if (found) return { id: Number(found.market_id), confirmed: true };
    } catch (e) {
      log?.(`indexer poll: ${e.message || e}`);
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
  return { id: expectedId, confirmed: false };
}

/**
 * Operator create page - visible to owner/create-admin or NFT right-holders.
 * Non-privileged creators must post a participation bond before submitting.
 * @param {{ wallet: object, log?: Function, onCreated?: (id: number) => void }} opts
 */
export async function renderCreatePage({ wallet, log, onCreated }) {
  const root = document.createElement("div");
  root.className = "create-page";

  const privileged = isPrivilegedCreator(wallet);
  let hasCreateRight = privileged;
  if (!privileged) {
    const moonlightPk = connectedMoonlightAccount(wallet);
    if (moonlightPk) {
      try {
        const dusk = await getChainApp();
        hasCreateRight = await walletHasCreateRight(dusk, moonlightPk);
      } catch {
        hasCreateRight = false;
      }
    }
  }

  if (!hasCreateRight) {
    root.append(
      EmptyState({
        title: "Create access required",
        description:
          "Claim the streak achievement NFT on Achievements, post a participation bond in Account settings, then return here to create events. Contract owner and create-admin wallets can create without the NFT.",
      })
    );
    const hint = document.createElement("p");
    hint.className = "hint";
    const owner = predictionMarketOwnerAddress();
    const admin = predictionMarketCreateAdminAddress();
    hint.textContent = `Owner: ${owner ? `${owner.slice(0, 12)}…` : "-"} · Create admin: ${admin ? `${admin.slice(0, 12)}…` : "-"}`;
    root.append(hint);
    return root;
  }

  let bondBlocked = false;
  let bondReadErr = null;
  if (!privileged) {
    try {
      const pkBytes = moonlightPkBytesFromWallet(wallet);
      if (pkBytes) {
        const dusk = await getChainApp();
        const bond = await readParticipationBondState(dusk, pkBytes);
        bondBlocked = bond.posted < bond.required;
      }
    } catch (e) {
      bondReadErr = e?.message || String(e);
    }
  }

  const header = document.createElement("div");
  header.className = "create-header";
  const title = document.createElement("h1");
  title.textContent = "Create event";
  header.append(title);
  header.append(
    Badge({
      variant: privileged ? "success" : bondBlocked ? "pending" : "success",
      label: privileged ? "Create access" : bondBlocked ? "Bond required" : "Create access",
    })
  );
  root.append(header);

  if (!privileged && bondBlocked) {
    const bondBanner = document.createElement("div");
    bondBanner.className = "create-feedback";
    const banner = ErrorBanner({
      message:
        "Post the creator participation bond in Account settings before creating an event.",
    });
    bondBanner.append(banner);
    const settingsLink = document.createElement("a");
    settingsLink.href = "#/settings";
    settingsLink.className = "link-btn";
    settingsLink.textContent = "Open Account settings";
    settingsLink.addEventListener("click", (e) => {
      e.preventDefault();
      location.hash = "#/settings";
    });
    bondBanner.append(settingsLink);
    root.append(bondBanner);
  } else if (!privileged && bondReadErr) {
    const bondBanner = document.createElement("div");
    bondBanner.className = "create-feedback";
    bondBanner.append(ErrorBanner({ message: bondReadErr }));
    root.append(bondBanner);
  }

  const intro = document.createElement("p");
  intro.className = "hint";
  intro.textContent = privileged
    ? "Seeds FPMM liquidity from this wallet and posts display metadata to the gateway."
    : "NFT right-holder path - seeds FPMM liquidity from this wallet and posts display metadata to the gateway.";
  root.append(intro);

  let tip = null;
  try {
    tip = await getBlockHeight();
  } catch {
    tip = null;
  }

  let allowedCategoryIds = [];
  try {
    const duskCats = await getChainApp();
    const raw = await duskCats.readContract({
      contract: "predictionMarket",
      functionName: "allowed_categories",
    });
    allowedCategoryIds = Array.isArray(raw) ? raw.map(Number) : [];
  } catch (e) {
    log?.(`allowed_categories: ${e?.message || e}`);
  }
  const categoryOptions = createCategoryOptions(allowedCategoryIds);

  const layout = document.createElement("div");
  layout.className = "create-layout";

  const form = document.createElement("section");
  form.className = "create-form dt-card";

  const previewCol = document.createElement("aside");
  previewCol.className = "create-preview-col";
  const previewLabel = document.createElement("div");
  previewLabel.className = "dt-th create-preview-label";
  previewLabel.textContent = "Live preview";
  const previewHost = document.createElement("div");
  previewHost.className = "create-preview-host";
  previewCol.append(previewLabel, previewHost);

  function fieldText(el) {
    const input = el.querySelector("input, select, textarea");
    return input?.value?.trim() ?? "";
  }

  function paintPreview() {
    previewHost.replaceChildren();
    const titleText = fieldText(titleField) || "Event title";
    const categoryId = Number(fieldText(categoryField));
    previewHost.append(
      renderWenEventCard({
        id: "-",
        title: titleText,
        category: onChainCategoryLabel(categoryId),
        marketCategory: Number.isInteger(categoryId) ? categoryId : null,
        yesPercent: 50,
        poolLabel: fieldText(liqField) ? `${fieldText(liqField)} DUSK` : "-",
        tradeCount: 0,
        status: "open",
        onClick: null,
      })
    );
  }

  const titleField = attachTip(
    TextField({
      id: "pm-create-title",
      label: "Title",
      value: tip
        ? `Will DuskDS testnet tip exceed ${tip + 50_000} by close?`
        : "Will DuskDS testnet tip exceed 5,100,000 by close?",
      required: true,
      onInput: () => paintPreview(),
    }),
    FIELD_TIPS.title
  );

  const descField = textAreaField({
    id: "pm-create-desc",
    label: "Description",
    tip: FIELD_TIPS.description,
    value:
      "WEN demo event (KYC off). Owner asserts YES before close; else defaults to NO. Bonded disputes go to owner review.",
    rows: 7,
  });
  descField.querySelector("textarea")?.addEventListener("input", () => paintPreview());

  const categoryField = attachTip(
    Select({
      id: "pm-create-category",
      label: "Category",
      value: "0",
      options: categoryOptions,
      hint:
        categoryOptions.length === 1
          ? "Only Uncategorized until the owner enables Macro / Crypto / Sports / Politics."
          : undefined,
      onChange: () => paintPreview(),
    }),
    FIELD_TIPS.category
  );

  const criteriaField = textAreaField({
    id: "pm-create-criteria",
    label: "Resolution criteria",
    tip: FIELD_TIPS.criteria,
    value:
      "YES if the condition in the title holds at or before close_block; else NO. Owner asserts YES or late-challenges default NO with evidence.",
    required: true,
    rows: 4,
  });

  const liqField = attachTip(
    TextField({
      id: "pm-create-liq",
      label: "Seed liquidity (DUSK)",
      value: "1",
      type: "number",
      min: 0,
      step: 0.001,
      onInput: () => paintPreview(),
    }),
    FIELD_TIPS.liquidity
  );

  const bondField = attachTip(
    TextField({
      id: "pm-create-bond",
      label: "Min dispute bond (DUSK)",
      value: "0.1",
      type: "number",
      min: 0,
      step: 0.001,
    }),
    FIELD_TIPS.minBond
  );

  const feeField = attachTip(
    TextField({
      id: "pm-create-fee",
      label: "LP fee (bps)",
      value: "0",
      type: "number",
      min: 0,
      max: 1000,
      step: 1,
    }),
    FIELD_TIPS.feeBps
  );

  const challengeField = attachTip(
    TextField({
      id: "pm-create-challenge",
      label: "Challenge window (blocks)",
      value: "500",
      type: "number",
      min: 1,
      step: 1,
    }),
    FIELD_TIPS.challenge
  );

  const reviewField = attachTip(
    TextField({
      id: "pm-create-review",
      label: "Review window (blocks)",
      value: "200",
      type: "number",
      min: 1,
      step: 1,
    }),
    FIELD_TIPS.review
  );

  // Gated behind ?debug=1, matching main.js's debug backend settings field
  // - this credential shouldn't be something an ordinary Create-tab visit
  // invites typing into a field that then gets written to localStorage in
  // plaintext (see submit()'s saveBackendConfig call below). Production
  // hosting injects the key via a proxy (see prediction-market-portal
  // README's Deploy section) - that's the only supported path outside
  // explicit debug use.
  const debugMode = new URLSearchParams(location.search).get("debug") === "1";
  const savedCfgEarly = loadBackendConfig();
  const apiKeyField = debugMode
    ? attachTip(
        TextField({
          id: "pm-create-apikey",
          label: "Gateway API key (debug)",
          type: "password",
          value: savedCfgEarly.apiKey || "",
        }),
        FIELD_TIPS.apiKey
      )
    : null;
  const metaUserField = debugMode
    ? attachTip(
        FormField({
          id: "pm-create-meta-user",
          label: "Metadata Basic user (debug)",
          value: savedCfgEarly.metadataBasicUser || "",
        }),
        FIELD_TIPS.metadataBasic
      )
    : null;
  const metaPassField = debugMode
    ? attachTip(
        FormField({
          id: "pm-create-meta-pass",
          label: "Metadata Basic password (debug)",
          type: "password",
          value: savedCfgEarly.metadataBasicPass || "",
        }),
        FIELD_TIPS.metadataBasic
      )
    : null;

  // --- Close block duration picker ---
  /** @type {"hours"|"days"|"weeks"|"date"|"epoch"|"blocks"} */
  let closeMode = "days";
  let closeAmount = "3";
  /** @type {"ahead"|"absolute"} */
  let blocksKind = "ahead";
  /** @type {"ahead"|"absolute"} */
  let epochKind = "ahead";
  let closeDateValue = "";

  const closeSection = document.createElement("div");
  closeSection.className = "create-close-section";

  const closeLabelRow = document.createElement("div");
  closeLabelRow.className = "create-label-with-tip create-section-label";
  const closeLabel = document.createElement("span");
  closeLabel.className = "form-label";
  closeLabel.textContent = "Close";
  closeLabelRow.append(closeLabel, tipBtn(FIELD_TIPS.close));
  closeSection.append(closeLabelRow);

  const modeRow = document.createElement("div");
  modeRow.className = "create-close-modes";
  modeRow.setAttribute("role", "group");
  modeRow.setAttribute("aria-label", "Close timing mode");

  const closeControls = document.createElement("div");
  closeControls.className = "create-close-controls";

  const closeReadout = document.createElement("p");
  closeReadout.className = "form-hint create-close-readout";

  function computeCloseBlock() {
    if (tip == null && closeMode !== "blocks") return null;
    if (closeMode === "date") {
      if (tip == null || !closeDateValue) return null;
      const target = new Date(closeDateValue).getTime();
      if (!Number.isFinite(target)) return null;
      const deltaSec = Math.max(0, (target - Date.now()) / 1000);
      return tip + Math.max(1, Math.round(deltaSec / BLOCK_SECONDS));
    }
    const n = Number(closeAmount);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (closeMode === "blocks") {
      if (blocksKind === "absolute") return Math.floor(n);
      if (tip == null) return null;
      return tip + Math.max(1, Math.floor(n));
    }
    if (closeMode === "epoch") {
      if (epochKind === "absolute") {
        return Math.floor(n) * BLOCKS_PER_EPOCH;
      }
      if (tip == null) return null;
      const epochsAhead = Math.max(1, Math.floor(n));
      return nextEpochStart(tip) + (epochsAhead - 1) * BLOCKS_PER_EPOCH;
    }
    if (tip == null) return null;
    const hours =
      closeMode === "hours" ? n : closeMode === "days" ? n * 24 : n * 24 * 7;
    return tip + Math.max(1, Math.round(hours * BLOCKS_PER_HOUR));
  }

  function paintCloseReadout() {
    const block = computeCloseBlock();
    if (tip == null && closeMode !== "blocks") {
      closeReadout.textContent = "Tip unavailable - cannot estimate close block.";
      return;
    }
    if (tip == null && closeMode === "blocks" && blocksKind === "ahead") {
      closeReadout.textContent = "Tip unavailable - use absolute block height.";
      return;
    }
    if (block == null) {
      const tipBit = tip != null ? `tip ${tip.toLocaleString()} · epoch ${epochOf(tip)}` : "tip ?";
      closeReadout.textContent = `Current ${tipBit} · set a close value.`;
      return;
    }
    const ep = epochOf(block);
    let when = "";
    if (tip != null && block > tip) {
      const ahead = block - tip;
      const hours = ahead / BLOCKS_PER_HOUR;
      when =
        hours < 48
          ? `~${Math.round(hours * 10) / 10}h`
          : hours < 24 * 14
            ? `~${Math.round((hours / 24) * 10) / 10}d`
            : `~${Math.round((hours / (24 * 7)) * 10) / 10}w`;
      when = ` · ${when} · +${ahead.toLocaleString()} blocks`;
    }
    const tipBit =
      tip != null ? ` · tip ${tip.toLocaleString()} (epoch ${epochOf(tip)})` : "";
    closeReadout.textContent = `Closes at block ${block.toLocaleString()} (epoch ${ep}${when}${tipBit})`;
  }

  function paintKindToggle(host, kind, onChange) {
    const row = document.createElement("div");
    row.className = "create-close-kind";
    row.setAttribute("role", "group");
    for (const [id, label] of [
      ["ahead", "From tip"],
      ["absolute", "Absolute"],
    ]) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `create-kind-btn${kind === id ? " is-active" : ""}`;
      btn.dataset.kind = id;
      btn.textContent = label;
      btn.addEventListener("click", () => {
        onChange(id);
        for (const child of row.children) {
          child.classList.toggle("is-active", child.dataset.kind === id);
        }
        paintCloseControls();
      });
      row.append(btn);
    }
    host.append(row);
  }

  function paintCloseControls() {
    closeControls.replaceChildren();
    if (closeMode === "date") {
      const dateField = TextField({
        id: "pm-create-close-date",
        label: "Close date & time",
        type: "datetime-local",
        value: closeDateValue,
        onInput: (v) => {
          closeDateValue = v;
          paintCloseReadout();
        },
      });
      closeControls.append(dateField);
    } else if (closeMode === "epoch") {
      paintKindToggle(closeControls, epochKind, (k) => {
        epochKind = /** @type {typeof epochKind} */ (k);
        closeAmount = epochKind === "absolute" && tip != null
          ? String(epochOf(tip) + 1)
          : closeAmount || "1";
      });
      const amountField = TextField({
        id: "pm-create-close-epoch",
        label: epochKind === "absolute" ? "Epoch number" : "Epochs ahead",
        type: "number",
        value: closeAmount,
        min: 1,
        step: 1,
        onInput: (v) => {
          closeAmount = v;
          paintCloseReadout();
        },
      });
      closeControls.append(amountField);
    } else if (closeMode === "blocks") {
      paintKindToggle(closeControls, blocksKind, (k) => {
        blocksKind = /** @type {typeof blocksKind} */ (k);
        closeAmount =
          blocksKind === "absolute" && tip != null
            ? String(tip + 10_000)
            : closeAmount || "10000";
      });
      const amountField = TextField({
        id: "pm-create-close-blocks",
        label: blocksKind === "absolute" ? "Close block" : "Blocks ahead",
        type: "number",
        value: closeAmount,
        min: 1,
        step: 1,
        onInput: (v) => {
          closeAmount = v;
          paintCloseReadout();
        },
      });
      closeControls.append(amountField);
    } else {
      const unitLabel =
        closeMode === "hours" ? "Hours" : closeMode === "days" ? "Days" : "Weeks";
      const amountField = TextField({
        id: "pm-create-close-amount",
        label: unitLabel,
        type: "number",
        value: closeAmount,
        min: 1,
        step: 1,
        onInput: (v) => {
          closeAmount = v;
          paintCloseReadout();
        },
      });
      closeControls.append(amountField);
    }
    paintCloseReadout();
  }

  const MODE_LABELS = {
    hours: "Hours",
    days: "Days",
    weeks: "Weeks",
    date: "Date",
    epoch: "Epoch",
    blocks: "Blocks",
  };

  for (const mode of ["hours", "days", "weeks", "date", "epoch", "blocks"]) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `create-mode-btn${closeMode === mode ? " is-active" : ""}`;
    btn.dataset.mode = mode;
    btn.textContent = MODE_LABELS[mode];
    btn.addEventListener("click", () => {
      closeMode = /** @type {typeof closeMode} */ (mode);
      if (mode === "epoch") closeAmount = "1";
      if (mode === "blocks") closeAmount = "10000";
      if (mode === "days") closeAmount = "3";
      for (const child of modeRow.children) {
        child.classList.toggle("is-active", child.dataset.mode === mode);
      }
      paintCloseControls();
    });
    modeRow.append(btn);
  }

  closeSection.append(modeRow, closeControls, closeReadout);
  paintCloseControls();

  // Layout
  form.append(titleField, descField, categoryField);

  form.append(criteriaField);

  const moneyRow = document.createElement("div");
  moneyRow.className = "create-row-2";
  moneyRow.append(liqField, bondField);
  form.append(moneyRow);

  form.append(closeSection);

  const windowsRow = document.createElement("div");
  windowsRow.className = "create-row-2";
  windowsRow.append(challengeField, reviewField);
  form.append(windowsRow);

  form.append(feeField);

  if (apiKeyField) form.append(apiKeyField);
  if (metaUserField) form.append(metaUserField);
  if (metaPassField) form.append(metaPassField);

  layout.append(form, previewCol);
  root.append(layout);
  paintPreview();

  const feedback = document.createElement("div");
  feedback.className = "create-feedback";
  root.append(feedback);

  const actionsHost = document.createElement("div");
  actionsHost.className = "create-actions";
  root.append(actionsHost);

  let busy = false;

  function fieldValue(el) {
    const input = el.querySelector("input, select, textarea");
    return input?.value?.trim() ?? "";
  }

  function setBusy(next) {
    busy = next;
    renderActions();
  }

  function showError(msg) {
    feedback.replaceChildren(ErrorBanner({ message: msg }));
  }

  function showOk(msg) {
    const p = document.createElement("p");
    p.className = "create-success";
    p.textContent = msg;
    feedback.replaceChildren(p);
  }

  async function submit() {
    if (!privileged && bondBlocked) {
      showError(
        "Post the creator participation bond in Account settings before creating an event."
      );
      return;
    }
    const titleText = fieldValue(titleField);
    const description = fieldValue(descField);
    const criteria = fieldValue(criteriaField);
    const liqDusk = Number(fieldValue(liqField));
    const closeBlock = computeCloseBlock();
    const challengeWindow = Number(fieldValue(challengeField));
    const reviewWindow = Number(fieldValue(reviewField));
    const bondDusk = Number(fieldValue(bondField));
    const feeBps = Number(fieldValue(feeField));
    const onChainCategory = Number(fieldValue(categoryField));
    const apiKey = apiKeyField ? fieldValue(apiKeyField) : null;
    const metadataBasicUser = metaUserField ? fieldValue(metaUserField) : null;
    const metadataBasicPass = metaPassField ? fieldValue(metaPassField) : null;

    if (!titleText) {
      showError("Title is required.");
      return;
    }
    if (!criteria) {
      showError("Resolution criteria are required.");
      return;
    }
    if (!Number.isFinite(liqDusk) || liqDusk <= 0) {
      showError("Seed liquidity must be a positive DUSK amount.");
      return;
    }
    if (closeBlock == null || !Number.isInteger(closeBlock) || closeBlock <= 0) {
      showError("Set a valid close duration or date (tip must be available).");
      return;
    }
    if (tip != null && closeBlock <= tip) {
      showError("Close block must be after the current tip.");
      return;
    }
    if (!Number.isInteger(challengeWindow) || challengeWindow <= 0) {
      showError("Challenge window must be a positive integer.");
      return;
    }
    if (!Number.isInteger(reviewWindow) || reviewWindow <= 0) {
      showError("Review window must be a positive integer.");
      return;
    }
    if (!Number.isFinite(bondDusk) || bondDusk < 0) {
      showError("Min bond must be a non-negative DUSK amount.");
      return;
    }
    if (!Number.isInteger(feeBps) || feeBps < 0 || feeBps > 1000) {
      showError("LP fee must be an integer between 0 and 1000 bps (10%).");
      return;
    }
    if (!Number.isInteger(onChainCategory) || onChainCategory < 0) {
      showError("Pick a category from the list.");
      return;
    }
    if (
      onChainCategory !== 0 &&
      !allowedCategoryIds.includes(onChainCategory)
    ) {
      showError("That category is not enabled on-chain.");
      return;
    }

    const liquidity = Number(duskToLux(liqDusk));
    const minBond = Number(duskToLux(bondDusk));
    const commitment = buildMarketCommitment({
      title: titleText,
      description,
      resolutionCriteria: criteria,
    });

    let expectedId = 0;
    // Only touch the persisted apiKey when the debug field actually
    // exists - outside ?debug=1 this leaves whatever's already configured
    // (normally nothing; production hosting proxy-injects the header
    // instead) alone rather than writing a null/empty value over it.
    const cfg = { ...loadBackendConfig() };
    if (apiKeyField) cfg.apiKey = apiKey;
    if (metaUserField) cfg.metadataBasicUser = metadataBasicUser;
    if (metaPassField) cfg.metadataBasicPass = metadataBasicPass;
    if (apiKeyField || metaUserField || metaPassField) saveBackendConfig(cfg);
    try {
      const markets = await listMarkets(cfg, { includeHidden: true });
      expectedId =
        markets.reduce((max, m) => Math.max(max, Number(m.market_id) || 0), -1) + 1;
    } catch {
      expectedId = 0;
    }

    setBusy(true);
    feedback.replaceChildren();
    let ui;
    try {
      const seedLabel = document.createDocumentFragment();
      seedLabel.append("Create event · seed ", duskAmountNode(liquidity));
      ui = showGlobalTx({ label: seedLabel });
      const dusk = await getChainApp();
      const tx = await dusk.writeContract({
        contract: "predictionMarket",
        functionName: "create_market",
        args: {
          question_hash: commitment.questionHash,
          content_cid: commitment.contentCid,
          close_block: closeBlock,
          challenge_window_blocks: challengeWindow,
          review_window_blocks: reviewWindow,
          initial_liquidity: liquidity,
          min_bond: minBond,
          kyc_override: null,
          fee_bps: feeBps,
          category: onChainCategory,
        },
        privacy: "public",
        deposit: String(liquidity),
      });
      const tracked = await trackTx(tx, { ui, autoDismissMs: 10_000 });
      log?.(
        `create_market tx ${tracked.hash} · qh=${commitment.questionHashHex.slice(0, 12)}…`
      );

      // Post metadata immediately by question_hash - enrich_markets joins on
      // hash, and market_id is optional on the gateway. Gating this on the
      // indexer poll left Event #N shells whenever confirmation lagged >~24s.
      const metaBody = {
        question_hash: commitment.questionHashHex,
        content_cid: commitment.contentCidHex,
        title: titleText,
        description,
        resolution_criteria: criteria,
        canonical_json: commitment.canonical,
      };
      try {
        await postMarketMetadata(cfg, metaBody);
        log?.(`metadata posted for qh=${commitment.questionHashHex.slice(0, 12)}…`);
      } catch (metaErr) {
        const msg = metaErr.message || String(metaErr);
        showError(
          `Event created on-chain (tx ${tracked.hash.slice(0, 12)}…) but metadata POST failed: ${msg}. Fix gateway auth (Basic Auth / API key), then backfill with scripts/pm-backfill-metadata.sh using the same title/description/criteria.`
        );
        log?.(`metadata failed: ${msg}`);
        return;
      }

      const { id: marketId, confirmed } = await waitForMarketId(
        cfg,
        commitment.questionHashHex,
        expectedId,
        log
      );

      if (confirmed) {
        // Bookkeeping only - enrich already matches by question_hash.
        try {
          await postMarketMetadata(cfg, { ...metaBody, market_id: marketId });
        } catch (e) {
          log?.(`metadata market_id attach skipped: ${e.message || e}`);
        }
        showOk(`Event #${marketId} created · closes at block ${closeBlock.toLocaleString()}.`);
        onCreated?.(marketId);
        return;
      }

      // Tx + metadata succeeded; indexer just hasn't listed the market yet.
      showOk(
        `Event created on-chain and metadata saved. The gateway at ${cfg.url} hasn't indexed the market_id yet - refresh Events in a moment (title should appear once the indexer catches up).`
      );
      log?.(`market_id not confirmed by indexer within poll window (metadata already posted)`);
    } catch (e) {
      const msg = mapCreateError(e);
      showError(msg);
      log?.(`create_market failed: ${msg}`);
      ui?.setStatus("failed", msg);
    } finally {
      setBusy(false);
    }
  }

  function renderActions() {
    const blocked = !privileged && bondBlocked;
    actionsHost.replaceChildren(
      ActionBar([
        Button({
          label: busy ? "Creating…" : "Create event",
          variant: "primary",
          disabled: busy || blocked,
          onClick: () => submit(),
        }),
      ])
    );
  }

  renderActions();
  return root;
}
